<template>
  <div v-if="activeDiscounts.length" class="discount-badges-container" :style="{ width: width }">
    <div v-for="discount in activeDiscounts" :key="discount.id" class="discount-badge" :class="[discount.offer_day == 0 ? 'dis-long-weekend' : 'dis-long-sejour', { 'expanded': expandedDiscount === discount.id }]">
      <div class="discount-header">
        <div class="discount-text-container">
          <span class="discount-offer-text">{{ getDiscountOfferText(discount) }}. </span></br>
          <span class="discount-validity-text">
            Valable pour tout séjour de plus de
            <span class="discount-nights-text">{{ getDiscountNightsText(discount) }}</span>
          </span>
        </div>
        <div class="discount-dates-toggle"
             @click="toggleDateExpansion(discount.id)">
          <span class="dates-toggle-text raleway-semi-p13">Voir les périodes de l'année</span>
          <i class="fas" :class="expandedDiscount === discount.id ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>
      </div>
      
      <!-- Expanded date ranges -->
      <div v-if="expandedDiscount === discount.id" class="discount-date-ranges">
        <ul class="date-ranges-list raleway-medi-p13">
          <li v-for="range in getDateRanges(discount)" :key="range" class="date-range-item">
            {{ range }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LodgingDiscountBadge",
  props: {
    lodging: {
      type: Object,
      required: true
    },
    width: {
      type: String,
      default: '100%'
    }
  },
  data() {
    return {
      expandedDiscount: null
    };
  },
  computed: {
    activeDiscounts() {
      if (!this.lodging.lodging_discounts || !this.lodging.lodging_discounts.length) {
        return [];
      }
      
      // Group discounts by category, discount percentage, and min_stay_nights
      const weekendDiscounts = this.lodging.lodging_discounts.filter(discount => discount.offer_day === 0);
      const longStayDiscounts = this.lodging.lodging_discounts.filter(discount => discount.offer_day !== 0);
      
      const result = [];
      
      // Process weekend discounts - group by discount percentage and min_stay_nights
      if (weekendDiscounts.length > 0) {
        const weekendGroups = {};
        weekendDiscounts.forEach(discount => {
          const percent = parseInt(discount.discount_percent);
          const minStayNights = discount.min_stay_nights || 0;
          const groupKey = `${percent}-${minStayNights}`;
          
          if (!weekendGroups[groupKey]) {
            weekendGroups[groupKey] = [];
          }
          weekendGroups[groupKey].push(discount);
        });
        
        // Create a badge for each unique weekend discount percentage and min_stay_nights combination
        Object.keys(weekendGroups).forEach(groupKey => {
          const discounts = weekendGroups[groupKey];
          const [percent, minStayNights] = groupKey.split('-').map(Number);
          
          result.push({
            id: `weekend-${percent}-${minStayNights}`,
            offer_day: 0,
            discount_percent: percent,
            min_stay_nights: minStayNights,
            discounts: discounts
          });
        });
      }
      
      // Process long stay discounts - group by discount percentage and min_stay_nights
      if (longStayDiscounts.length > 0) {
        const longStayGroups = {};
        longStayDiscounts.forEach(discount => {
          const percent = parseInt(discount.discount_percent);
          const minStayNights = discount.min_stay_nights || 0;
          const groupKey = `${percent}-${minStayNights}`;
          
          if (!longStayGroups[groupKey]) {
            longStayGroups[groupKey] = [];
          }
          longStayGroups[groupKey].push(discount);
        });
        
        // Create a badge for each unique long stay discount percentage and min_stay_nights combination
        Object.keys(longStayGroups).forEach(groupKey => {
          const discounts = longStayGroups[groupKey];
          const [percent, minStayNights] = groupKey.split('-').map(Number);
          
          result.push({
            id: `longstay-${percent}-${minStayNights}`,
            offer_day: 1,
            discount_percent: percent,
            min_stay_nights: minStayNights,
            discounts: discounts
          });
        });
      }
      
      // Sort by discount percentage descending
      return result.sort((a, b) => b.discount_percent - a.discount_percent);
    }
  },
  methods: {
    getDiscountOfferText(discount) {
      const baseText = discount.offer_day === 0 ? 'Offre long week-end' : 'Offre long séjour';
      return `${baseText} -${parseInt(discount.discount_percent)}%`;
    },
    getDiscountNightsText(discount) {
      return discount.min_stay_nights && discount.min_stay_nights > 0 ? `${discount.min_stay_nights - 1} nuits` : `${discount.offer_day === 0 ? '2' : '7'} nuits`;
    },
    toggleDateExpansion(discountId) {
      this.expandedDiscount = this.expandedDiscount === discountId ? null : discountId;
    },
    getDateRanges(discount) {
      const monthNames = [
        'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
        'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
      ];
      
      const ranges = [];
      
      // Process all discounts with the same max percentage
      discount.discounts.forEach(d => {
        const startDate = new Date(d.start_date);
        const endDate = new Date(d.end_date);
        
        const startMonth = monthNames[startDate.getMonth()];
        const endMonth = monthNames[endDate.getMonth()];
        
        if (startMonth === endMonth) {
          ranges.push(`En ${startMonth}`);
        } else {
          ranges.push(`De ${startMonth} à ${endMonth}`);
        }
      });
      
      // Remove duplicates and sort
      const uniqueRanges = [...new Set(ranges)];
      return uniqueRanges.sort();
    }
  }
};
</script>

<style lang="scss" scoped>
.discount-badges-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
  padding: 0 16px;
}

.discount-badge {
  width: 100%;
  min-height: 30px;
  background-color: #A5D3AE;
  border-radius: 24px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  opacity: 1;
  position: relative;
}

.discount-text-container {
  text-align: left;
  flex: 1;
}

.discount-offer-text {
  font-family: Raleway;
  font-weight: 700;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  color: #3B6152;
}

.discount-validity-text {
  font-family: Raleway;
  font-weight: 500;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  color: #3B6152;
}

.discount-nights-text {
  font-family: Raleway;
  font-weight: 700;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  color: #3B6152;
}

.dis-long-weekend {
  background-color: #A5D3AE;
}

.dis-long-sejour {
  background-color: #CFE5DC;
}

.discount-dates-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  flex-shrink: 0;
  
  .dates-toggle-text {
    color: #000;
    text-decoration: underline;
    white-space: nowrap;
  }
  
  i {
    font-size: 10px;
    color: #000;
    transition: transform 0.2s ease;
  }
}

.discount-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;

  @media screen and (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px; 
  }
}

.discount-badge.expanded {
  flex-direction: column;
  align-items: stretch;
  padding-bottom: 16px;
  
  .discount-text-container {
    text-align: left;
  }
}

.discount-date-ranges {
  width: 100%;
}

.date-ranges-list {
  list-style: none;
  padding: 0;
  margin: 0;
  
  .date-range-item {
    color: #3B6152;
    padding: 4px 0;
    position: relative;
    padding-left: 16px;
    
    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: #3B6152;
      font-weight: bolder;
    }
  }
}
</style>